import { useState } from 'react';
import type {
  BillingCycleHorizon,
  SolTemporalKpiSnapshot,
} from '@economi/domain-contracts';
import {
  computeMultiCycleCardSummary,
  formatSolesFromCents,
  MAX_USER_CREDIT_CARDS,
  type FinancialCycleState,
} from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './PrimaryCapacityHero.module.css';

export interface LastRegisteredExpenseFeedback {
  readonly merchantName: string;
  readonly expenseAmountCents: number;
  readonly capacityPercentConsumed: number;
  readonly remainingCapCents: number;
  readonly remainingCapacityPercent: number;
  readonly effectiveSpendingCapCents: number;
  readonly cardAlias?: string | null;
  readonly billingCycleHorizon?: BillingCycleHorizon;
}

interface PrimaryCapacityHeroProps {
  readonly cycleState: FinancialCycleState;
  readonly snapshot: SolTemporalKpiSnapshot;
  readonly lastRegisteredImpact: LastRegisteredExpenseFeedback | null;
  readonly onDismissLastImpact: () => void;
  readonly onOpenSetupFlow: () => void;
  readonly onOpenCardsConfig?: () => void;
  readonly onOpenRecurringExpensesFlow?: () => void;
  readonly onOpenCameraCaptureFlow: () => void;
  readonly showDetailedKpis: boolean;
  readonly onToggleDetailedKpis: () => void;
}

export function PrimaryCapacityHero({
  cycleState,
  snapshot,
  lastRegisteredImpact,
  onDismissLastImpact,
  onOpenSetupFlow,
  onOpenCardsConfig,
  onOpenRecurringExpensesFlow,
  onOpenCameraCaptureFlow,
}: PrimaryCapacityHeroProps) {
  const [isBalanceHidden, setIsBalanceHidden] = useState(false);
  const remainingPercent = snapshot.remainingSpendingCapacityPercent;
  const spentPercent = snapshot.spentCapacityPercent;
  const multiCycle = computeMultiCycleCardSummary(cycleState, snapshot);

  const percentToneClass =
    remainingPercent <= 15
      ? styles.metricPercentDanger
      : remainingPercent <= 35
        ? styles.metricPercentWarning
        : styles.metricPercentAccent;

  const fillToneClass =
    remainingPercent <= 15
      ? styles.fillDanger
      : remainingPercent <= 35
        ? styles.fillWarning
        : styles.fillOptimal;

  return (
    <section
      className={styles.heroWrapper}
      aria-label="Capacidad de Gasto y Tarjetas"
    >
      <div className={styles.primaryCard}>
        {lastRegisteredImpact && (
          <div className={styles.postExpenseTop} role="status">
            <span className={styles.postExpenseBadge}>
              ✅ {lastRegisteredImpact.merchantName} (-
              {formatSolesFromCents(lastRegisteredImpact.expenseAmountCents)}
              {lastRegisteredImpact.cardAlias
                ? ` · 💳 ${lastRegisteredImpact.cardAlias}`
                : ''}
              {lastRegisteredImpact.billingCycleHorizon === 'NEXT_CYCLE'
                ? ' · 🔵 Próximo Ciclo'
                : ` · -${lastRegisteredImpact.capacityPercentConsumed.toFixed(1)}%`}
              )
            </span>
            <button
              type="button"
              className={styles.dismissBtn}
              onClick={onDismissLastImpact}
              aria-label="Ocultar aviso"
            >
              ×
            </button>
          </div>
        )}

        <div className={styles.headerRow}>
          <div className={styles.headerIntroText}>
            <span className={styles.eyebrow}>
              CICLO AL {snapshot.renewalDateIso} · {snapshot.remainingDays} DÍAS RESTANTES
            </span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
              <h2 className={styles.title}>Capacidad de Gasto Disponible</h2>
              <InfoPopover title="¿Cómo se calcula tu Capacidad Disponible?">
                <span>
                  Tu tope de gasto ({snapshot.spendingRatePercent}% de tus ingresos ={' '}
                  <strong>{formatSolesFromCents(snapshot.effectiveSpendingCapCents)}</strong>) es el{' '}
                  <strong>100% permitido</strong> hasta tu próximo ingreso ({snapshot.renewalDateIso}).
                </span>
                <span>
                  Los gastos recurrentes vigentes se descuentan desde el Día 1 y el resto queda como ahorro protegido ({formatSolesFromCents(snapshot.protectedSavingsCents)}).
                </span>
              </InfoPopover>
            </div>
          </div>

          <div className={styles.flowShortcuts}>
            <button
              type="button"
              className={styles.flowBtnSecondary}
              onClick={onOpenCardsConfig ?? onOpenSetupFlow}
            >
              💳 Ciclo y Tarjetas
            </button>
            {onOpenRecurringExpensesFlow && (
              <button
                type="button"
                className={styles.flowBtnSecondary}
                onClick={onOpenRecurringExpensesFlow}
              >
                🔄 Recurrentes
              </button>
            )}
            <button
              type="button"
              className={`${styles.flowBtnPrimary} ${styles.mobileCameraCta}`}
              onClick={onOpenCameraCaptureFlow}
            >
              📷 Escanear Boleta
            </button>
          </div>
        </div>

        {snapshot.totalInflowCents === 0 && (
          <div className={styles.emptySetupPrompt}>
            <div className={styles.emptySetupText}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                👋 Cuenta en S/ 0.00 — Configura tu ciclo
                <InfoPopover title="Primeros pasos">
                  Registra tu ingreso, tus tarjetas (máx. 3) con sus fechas de facturación/pago y tus gastos recurrentes para activar el monitoreo.
                </InfoPopover>
              </strong>
            </div>
            <div className={styles.emptySetupActions}>
              <button
                type="button"
                className={styles.flowBtnPrimary}
                onClick={onOpenSetupFlow}
              >
                💰 Ciclo y Tarjetas
              </button>
              {onOpenRecurringExpensesFlow && (
                <button
                  type="button"
                  className={styles.flowBtnSecondary}
                  onClick={onOpenRecurringExpensesFlow}
                >
                  🔄 Recurrentes
                </button>
              )}
            </div>
          </div>
        )}

        <div className={styles.metricsRow}>
          <div className={styles.metricBlock}>
            <div className={styles.metricLabelRow}>
              <span
                className={styles.metricLabel}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
              >
                DISPONIBLE EN SOLES
                <InfoPopover title="Saldo Disponible del Ciclo">
                  Monto que aún puedes gastar en este ciclo sin superar tu tope de{' '}
                  {formatSolesFromCents(snapshot.effectiveSpendingCapCents)}.
                </InfoPopover>
              </span>
              <button
                type="button"
                className={styles.privacyToggleBtn}
                onClick={() => setIsBalanceHidden((prev) => !prev)}
                aria-label={isBalanceHidden ? 'Mostrar saldos' : 'Ocultar saldos'}
              >
                {isBalanceHidden ? '🙈 Mostrar' : '👁️ Privacidad'}
              </button>
            </div>
            <span className={styles.metricBigValue}>
              {isBalanceHidden
                ? 'S/ ••••••'
                : formatSolesFromCents(snapshot.remainingCapCents)}
            </span>
            <span className={styles.metricSubtext}>
              Usado:{' '}
              {isBalanceHidden
                ? 'S/ ••••'
                : formatSolesFromCents(snapshot.totalSpentCents)}{' '}
              de{' '}
              {isBalanceHidden
                ? 'S/ ••••'
                : formatSolesFromCents(snapshot.effectiveSpendingCapCents)}
            </span>
          </div>

          <div className={styles.metricBlock}>
            <span
              className={styles.metricLabel}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            >
              % DISPONIBLE DEL LÍMITE
              <InfoPopover title="% Disponible y Cuota Diaria">
                Porcentaje libre de tu 100% permitido y monto diario seguro para los {snapshot.remainingDays} días restantes.
              </InfoPopover>
            </span>
            <span className={`${styles.metricBigValue} ${percentToneClass}`}>
              {remainingPercent.toFixed(1)}%
            </span>
            <span className={styles.metricSubtext}>
              {spentPercent.toFixed(1)}% usado · Cuota hoy:{' '}
              <strong>
                {isBalanceHidden
                  ? 'S/ •••/día'
                  : `${formatSolesFromCents(snapshot.dynamicSafeDailyAllowanceCents)}/día`}
              </strong>
            </span>
          </div>
        </div>

        <div className={styles.progressSection}>
          <div className={styles.progressLabels}>
            <span>
              Libre: <strong>{remainingPercent.toFixed(1)}%</strong> (
              {isBalanceHidden
                ? 'S/ ••••••'
                : formatSolesFromCents(snapshot.remainingCapCents)}
              )
            </span>
            <span>
              Tope: {isBalanceHidden ? 'S/ ••••' : formatSolesFromCents(snapshot.effectiveSpendingCapCents)} · Ahorro:{' '}
              {isBalanceHidden ? 'S/ ••••' : formatSolesFromCents(snapshot.protectedSavingsCents)}
            </span>
          </div>
          <div className={styles.progressBarTrack}>
            <div
              className={`${styles.progressBarFill} ${fillToneClass}`}
              style={{
                width: `${Math.max(0, Math.min(100, remainingPercent))}%`,
              }}
            />
          </div>
        </div>

        {/* Panel: Control Inteligente de Tarjetas (Máx. 3) & Monitor Multi-Ciclo */}
        <div className={styles.cardsControlPanel}>
          <div className={styles.cardsPanelHeader}>
            <div>
              <span className={styles.cardsPanelEyebrow}>
                MIS TARJETAS ({multiCycle.cards.length}/{MAX_USER_CREDIT_CARDS}) · MULTI-CICLO
              </span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
                <h3 className={styles.cardsPanelTitle}>
                  💳 Tarjetas, Fechas de Corte y Asesor
                </h3>
                <InfoPopover title="Control Multi-Ciclo de Tarjetas">
                  <span>
                    Las compras realizadas <strong>hasta el día de facturación</strong> se descuentan del Ciclo Actual.
                  </span>
                  <span>
                    Las compras posteriores al cierre de facturación pasan automáticamente al <strong>Próximo Ciclo</strong> sin reducir tu disponible de este mes.
                  </span>
                </InfoPopover>
              </div>
            </div>
            <button
              type="button"
              className={styles.configCardsBtn}
              onClick={onOpenCardsConfig ?? onOpenSetupFlow}
            >
              ⚙️ Gestionar ({multiCycle.cards.length}/{MAX_USER_CREDIT_CARDS})
            </button>
          </div>

          {/* 1. Asesor Inteligente Conciso + InfoPopover */}
          <div
            className={`${styles.advisorBanner} ${
              multiCycle.advisor.recommendedTargetHorizon === 'NEXT_CYCLE'
                ? styles.advisorBannerNextCycle
                : ''
            }`}
          >
            <div className={styles.advisorMain}>
              <div className={styles.advisorTopRow}>
                <span className={styles.advisorBadge}>
                  {multiCycle.advisor.hasCardsConfigured
                    ? multiCycle.advisor.recommendedTargetHorizon === 'NEXT_CYCLE'
                      ? '🔵 SUGERENCIA: PRÓXIMO CICLO'
                      : '🟢 SUGERENCIA: CICLO ACTUAL'
                    : '💳 TARJETAS'}
                </span>
                <span className={styles.advisorDirectSpendInfo}>
                  💳 Ciclo Actual:{' '}
                  <strong>
                    {formatSolesFromCents(multiCycle.currentCycleCardSpentCents)}
                  </strong>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
                <strong className={styles.advisorHeadline}>
                  {multiCycle.advisor.headline}
                </strong>
                <InfoPopover title="Análisis del Asesor de Tarjetas">
                  <span>{multiCycle.advisor.detailMessage}</span>
                  {multiCycle.advisor.tacticalStrategyNote && (
                    <span>
                      <strong>Estrategia:</strong> {multiCycle.advisor.tacticalStrategyNote}
                    </span>
                  )}
                </InfoPopover>
              </div>
            </div>
          </div>

          {/* Alertas de tarjetas que llegaron a su tope en el presente ciclo */}
          {multiCycle.advisor.cardsAtLimitAlerts.length > 0 && (
            <div className={styles.limitAlertsStack}>
              {multiCycle.advisor.cardsAtLimitAlerts.map((alert) => (
                <div key={alert.cardId} className={styles.limitAlertItem}>
                  <span>{alert.alertMessage}</span>
                </div>
              ))}
            </div>
          )}

          {/* 2. Tarjetas Configuradas (hasta 3 tarjetas) */}
          {multiCycle.cards.length === 0 ? (
            <div className={styles.noCardsBox}>
              <div className={styles.noCardsText}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  Sin tarjetas registradas (máx. {MAX_USER_CREDIT_CARDS})
                  <InfoPopover title="¿Por qué registrar tus tarjetas?">
                    Con el Día de Facturación y Día de Pago sabrás hasta qué fecha gastar en este ciclo y qué compras pasan al Próximo Ciclo.
                  </InfoPopover>
                </strong>
              </div>
              <button
                type="button"
                className={styles.flowBtnPrimary}
                onClick={onOpenCardsConfig ?? onOpenSetupFlow}
              >
                + Añadir Tarjetas
              </button>
            </div>
          ) : (
            <div className={styles.cardsGrid}>
              {multiCycle.cards.map((item) => {
                const isRecommended =
                  multiCycle.advisor.recommendedCardId === item.card.id;
                const usedPct = Math.max(
                  0,
                  Math.min(100, item.usedPercentOfCardLimit)
                );

                return (
                  <div
                    key={item.card.id}
                    className={`${styles.cardItemBox} ${
                      isRecommended ? styles.cardItemRecommended : ''
                    } ${
                      item.hasReachedLimitInCurrentCycle
                        ? styles.cardItemAtLimit
                        : ''
                    }`}
                    style={{
                      borderTopColor: item.card.colorHex || '#86f2e4',
                    }}
                  >
                    <div className={styles.cardItemTop}>
                      <div>
                        <div className={styles.cardAliasRow}>
                          <span
                            className={styles.cardColorDot}
                            style={{
                              backgroundColor: item.card.colorHex || '#86f2e4',
                            }}
                          />
                          <strong className={styles.cardAlias}>
                            {item.card.alias}
                          </strong>
                          {isRecommended && (
                            <span className={styles.recommendedPill}>
                              ⭐ Sugerida
                            </span>
                          )}
                        </div>
                        <span className={styles.cardBankSub}>
                          {item.card.issuerBank || 'Tarjeta de Crédito'}
                        </span>
                      </div>

                      <span
                        className={
                          item.isBillingOpenToday
                            ? styles.billingOpenBadge
                            : styles.billingClosedBadge
                        }
                      >
                        {item.isBillingOpenToday
                          ? '🟢 Abierta'
                          : '🔵 Cerrada (Próx. Ciclo)'}
                      </span>
                    </div>

                    <div className={styles.cardChipRow}>
                      <div
                        className={styles.cardChipEmulator}
                        aria-hidden="true"
                      />
                      <span className={styles.cardMaskedNumber}>
                        •••• •••• •••• {item.card.lastFourDigits || '••••'}
                      </span>
                      <span
                        className={styles.cardContactlessIcon}
                        aria-hidden="true"
                      >
                        )))
                      </span>
                    </div>

                    <div className={styles.cardDatesHighlightBox}>
                      <div className={styles.cardDateLine}>
                        <span>📅 Gastar en este ciclo hasta:</span>
                        <strong>{item.currentCycleBillingCloseDateIso}</strong>
                      </div>
                      <div className={styles.cardDateLine}>
                        <span>🏦 Fecha de Pago:</span>
                        <strong>{item.currentCyclePaymentDueDateIso}</strong>
                      </div>
                    </div>

                    <div className={styles.cardSpendProgressBlock}>
                      <div className={styles.cardSpendAmountsRow}>
                        <span>
                          Ciclo Actual:{' '}
                          <strong>
                            {formatSolesFromCents(item.currentCycleSpentCents)}
                          </strong>{' '}
                          / {formatSolesFromCents(item.effectiveCardLimitCents)}
                        </span>
                        <span
                          className={
                            item.hasReachedLimitInCurrentCycle
                              ? styles.cardPctDanger
                              : styles.cardPctNormal
                          }
                        >
                          {item.usedPercentOfCardLimit.toFixed(1)}%
                        </span>
                      </div>

                      <div className={styles.cardMiniBarTrack}>
                        <div
                          className={`${styles.cardMiniBarFill} ${
                            item.hasReachedLimitInCurrentCycle
                              ? styles.fillDanger
                              : usedPct >= 80
                                ? styles.fillWarning
                                : styles.fillOptimal
                          }`}
                          style={{ width: `${usedPct}%` }}
                        />
                      </div>

                      <div className={styles.cardSubBreakdownRow}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                          🔄 Recurrentes:{' '}
                          <strong>
                            {formatSolesFromCents(
                              item.currentCycleRecurringOnCardCents
                            )}
                          </strong>
                          <InfoPopover
                            title="Recurrentes en Tarjeta"
                            variant="onDark"
                          >
                            Incluidos en el consumo de esta tarjeta sin restarse dos veces de tu disponible global.
                          </InfoPopover>
                        </span>
                        <span className={styles.cardNextCycleTag}>
                          ⏭️ Próx. Ciclo:{' '}
                          <strong>
                            {formatSolesFromCents(item.nextCycleSpentCents)}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 3. Monitor del Próximo Ciclo */}
          <div className={styles.nextCycleMonitorBar}>
            <div className={styles.nextCycleMonitorLeft}>
              <span
                className={styles.nextCycleMonitorTitle}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
              >
                ⏭️ Monitor del Próximo Ciclo
                <InfoPopover title="Compras Post-Facturación">
                  Gastos realizados con tarjetas cuya fecha de facturación ya cerró. No reducen tu disponible del ciclo actual y se pagarán en el siguiente período.
                </InfoPopover>
              </span>
            </div>
            <div className={styles.nextCycleMonitorRight}>
              <span className={styles.nextCycleAmountBig}>
                {formatSolesFromCents(multiCycle.nextCycleCommittedCents)}
              </span>
              <span className={styles.nextCycleShareBadge}>
                {multiCycle.nextCycleShareOfCapPercent.toFixed(1)}% del tope
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
